/**
 * Normaliza a URL de recuperação de senha antes do vue-router iniciar.
 *
 * O app usa hash mode (`/#/usuario/senha/reset?token=...`), mas o link que sai
 * no e-mail nem sempre chega assim: clientes de e-mail e redirecionadores de
 * rastreio costumam escapar o `#` como `%23` ou reescrever o fragmento como
 * path. Sem hash, o router cai na tela de login e o token é perdido.
 */

const RECOVERY_PATHS = ['/usuario/senha/reset', '/usuario/senha/esqueceu'];
const DEFAULT_RECOVERY_PATH = '/usuario/senha/reset';

/** `/%23/usuario/senha/reset/` -> `/usuario/senha/reset` */
function normalizePath(rawPath) {
  let path = rawPath || '/';
  try {
    path = decodeURIComponent(path);
  } catch (error) {
    /* URL com escape inválido: segue com o valor original */
  }
  // Remove barras e `#` iniciais de uma vez: `/#/x` e `//x` viram `/x`.
  path = '/' + path.replace(/^[/#]+/, '');
  const withoutTrailingSlash = path.replace(/\/+$/, '');
  return withoutTrailingSlash || '/';
}

function matchRecoveryPath(path) {
  const lower = path.toLowerCase();
  return RECOVERY_PATHS.indexOf(lower) !== -1 ? lower : null;
}

/**
 * Reescreve a URL atual para o formato hash quando ela aponta para uma tela de
 * recuperação de senha sem hash. Executa antes do `new Vue`, então o router já
 * inicia na rota certa e o `?token=` continua acessível em `$route.query`.
 *
 * @returns {boolean} true se a URL foi reescrita.
 */
export function normalizeRecoveryUrl() {
  try {
    const loc = globalThis.window.location;
    // O construtor do VueRouter (hash mode) já pode ter normalizado o hash
    // vazio para `#/`, então `/` conta como "sem rota no hash".
    const hashPath = (loc.hash || '').replace(/^#/, '').trim();
    if (hashPath && hashPath !== '/') {
      return false;
    }

    const path = normalizePath(loc.pathname);
    const search = loc.search || '';
    let recoveryPath = matchRecoveryPath(path);

    // Redirecionador de e-mail que descarta o fragmento e preserva a query:
    // `?token=` só existe no link de reset, então é seguro tratar como reset.
    if (!recoveryPath && path === '/' && /[?&]token=/.test(search)) {
      recoveryPath = DEFAULT_RECOVERY_PATH;
    }

    if (!recoveryPath) {
      return false;
    }

    const target = loc.origin + '/#' + recoveryPath + search;
    if (globalThis.window.history && globalThis.window.history.replaceState) {
      globalThis.window.history.replaceState(null, '', target);
    } else {
      loc.replace(target);
    }
    return true;
  } catch (error) {
    return false;
  }
}
